"use client";

// socket.io built-in reconnect is disabled (`reconnection: false`). The custom
// scheduleReconnect() is the sole reconnect controller with bounded backoff (RECONNECT_BACKOFF_MS).
// Without this, socket.io's default reconnect ran in parallel — causing double attempts and duplicate words.
// sourceLanguage is locked on first CONNECT via lockedLanguageRef so reconnects keep the same query string.

import { useEffect, useRef, useState, useCallback } from "react";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import { LIVE_URL } from "@/config/constants";
import { speakerDisplayName } from "@/lib/utils/speaker-name";

/** Word entity from the transcription service */
export interface LiveWordEntity {
  text: string;
  speakerId?: string | number;
  confidence?: number;
  language?: string;
  instances?: {
    startInSec: number;
    endInSec: number;
  };
}

/** A paragraph of accumulated words grouped by speaker */
export interface LiveParagraph {
  id: number;
  text: string;
  speakerId: string;
  speakerName: string;
  language: string;
  startTime: number;
  endTime: number;
  words: LiveWordEntity[];
}

export interface UseLiveTranscriptOptions {
  /** API key for authentication with the live transcription service */
  apiKey: string;
  /** Source language code (e.g., 'en-US') */
  sourceLanguage: string;
  /** Media type being transcribed */
  mediaType: "audio" | "video";
  /** Whether the connection should be active */
  enabled: boolean;
  /** Optional: existing mediaId to subscribe to (for playback transcription) */
  mediaId?: string;
  /** Optional: folder the live media is created in; the server's default folder when empty */
  folderId?: string;
}

export interface UseLiveTranscriptReturn {
  paragraphs: LiveParagraph[];
  isConnected: boolean;
  isComplete: boolean;
  error: string | null;
  /** Media metadata returned by the server */
  mediaData: { mediaId: string; folderId: string; name: string } | null;
  /** Reconnect lifecycle for banner branching. T5 consumes this. */
  reconnectStatus: "idle" | "reconnecting" | "exhausted";
  /** True while the server's copy of the current take may be missing audio, so the local recording must be kept. */
  hasUndeliveredAudio: boolean;
  /** Every live media id the server created for the current take; a reconnect on a new server session adds one. */
  takeMediaIds: string[];
  /** Send audio data to the transcription service */
  sendAudioData: (audioBuffer: ArrayBuffer) => void;
  /** Stop live transcription */
  stopTranscription: () => void;
  /** Clear accumulated words */
  clearWords: () => void;
}

// About 90 s of retries in total, the time the live service holds a dropped session for a reconnect
const RECONNECT_BACKOFF_MS: readonly number[] = [500, 1500, 4500, ...Array<number>(16).fill(5000)];
// 1 s audio chunks kept until the server acks them; covers drop detection plus the 90 s hold
const MAX_UNACKED_CHUNKS = 150;
// Time after stop-transcription for the trailing words to arrive before the socket is closed
const STOP_GRACE_MS = 5000;
// Client-side disconnect reasons caused by the network; socket.io can recover these
const NETWORK_DISCONNECT_REASONS = new Set(["transport close", "transport error", "ping timeout"]);

// Socket.IO event names
const EVENTS = {
  START_LIVE_STREAM: "start-live-stream",
  AUDIO_DATA: "audio-data",
  STOP_TRANSCRIPTION: "stop-transcription",
  SUBSCRIBE_TO_MEDIA: "subscribe-to-media",
  UNSUBSCRIBE_FROM_MEDIA: "unsubscribe-from-media",
} as const;

// The hook re-sends these itself after a recovered reconnect, in order; socket.io's buffered copies would jump ahead
const EVENTS_RESENT_BY_HOOK = new Set<string>([EVENTS.AUDIO_DATA, EVENTS.STOP_TRANSCRIPTION]);

const RESPONSE_TYPES = {
  CONNECT: "connect",
  CONNECT_ERROR: "connect_error",
  DISCONNECT: "disconnect",
  ERROR: "error",
  METADATA: "metadata",
  TRANSCRIPT: "transcript",
  CLOSE: "close",
} as const;

// Paragraph breaking thresholds
const MIN_WORDS_PER_PARAGRAPH = 50;
const SOFT_MAX_WORDS_PER_PARAGRAPH = 100;

function processWord(
  word: LiveWordEntity,
  paragraphs: LiveParagraph[],
  currentParagraph: LiveParagraph | undefined,
): {
  paragraphs: LiveParagraph[];
  currentParagraph: LiveParagraph | undefined;
} {
  const speakerId = word.speakerId != null ? String(word.speakerId) : "";
  const language = word.language || "en-US";
  const startTime = word.instances?.startInSec ?? 0;
  const endTime = word.instances?.endInSec ?? 0;

  const isNewParagraph =
    !currentParagraph ||
    currentParagraph.speakerId !== speakerId ||
    currentParagraph.language !== language;

  if (isNewParagraph) {
    const newParagraph: LiveParagraph = {
      id: paragraphs.length + 1,
      text: word.text,
      speakerId,
      speakerName: speakerId ? speakerDisplayName(speakerId) : "",
      language,
      startTime,
      endTime,
      words: [word],
    };
    return {
      paragraphs: [...paragraphs, newParagraph],
      currentParagraph: newParagraph,
    };
  }

  const updatedParagraph: LiveParagraph = {
    ...currentParagraph,
    text: `${currentParagraph.text} ${word.text}`,
    endTime,
    words: [...currentParagraph.words, word],
  };

  const updatedParagraphs = [...paragraphs];
  updatedParagraphs[updatedParagraphs.length - 1] = updatedParagraph;

  const wordCount = updatedParagraph.words.length;
  let shouldClose = false;

  if (wordCount >= MIN_WORDS_PER_PARAGRAPH) {
    const lastChar = updatedParagraph.text.trimEnd().slice(-1);
    const isSentenceEnd =
      lastChar === "." || lastChar === "?" || lastChar === "!";
    const isAnyPunctuation =
      isSentenceEnd || lastChar === "," || lastChar === ";" || lastChar === ":";

    if (isSentenceEnd) {
      shouldClose = true;
    }
    if (
      !shouldClose &&
      wordCount >= SOFT_MAX_WORDS_PER_PARAGRAPH &&
      isAnyPunctuation
    ) {
      shouldClose = true;
    }
  }

  return {
    paragraphs: updatedParagraphs,
    currentParagraph: shouldClose ? undefined : updatedParagraph,
  };
}

export function useLiveTranscript({
  apiKey,
  sourceLanguage,
  mediaType,
  enabled,
  mediaId,
  folderId,
}: UseLiveTranscriptOptions): UseLiveTranscriptReturn {
  const [paragraphs, setParagraphs] = useState<LiveParagraph[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mediaData, setMediaData] = useState<{
    mediaId: string;
    folderId: string;
    name: string;
  } | null>(null);
  const [reconnectStatus, setReconnectStatus] = useState<
    "idle" | "reconnecting" | "exhausted"
  >("idle");
  const [hasUndeliveredAudio, setHasUndeliveredAudio] = useState(false);
  const [takeMediaIds, setTakeMediaIds] = useState<string[]>([]);

  const socketRef = useRef<Socket | null>(null);
  const paragraphsRef = useRef<LiveParagraph[]>([]);
  const currentParagraphRef = useRef<LiveParagraph | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectStatusRef = useRef<"idle" | "reconnecting" | "exhausted">("idle");
  // Locked on first CONNECT so reconnects reuse the same language query param.
  const lockedLanguageRef = useRef<string | null>(null);
  // Dedup words by (speakerId, startInSec) to suppress server retransmits after reconnect.
  const seenTuplesRef = useRef<Set<string>>(new Set());
  // While true, disconnect must NOT trigger scheduleReconnect (session intentionally over).
  // Cleared by clearWords() before the next session.
  const deliberateStopRef = useRef(false);
  // Stop was pressed while the socket was down: a recovered reconnect delivers the unacked tail, then stops.
  const pendingStopRef = useRef(false);
  const stopGraceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Chunks below this seq went out before stop-transcription, so the take is complete only once they are acked.
  const stopSeqRef = useRef<number | null>(null);
  // Audio chunks the server has not acked yet, oldest first; re-sent after a recovered reconnect.
  const unackedChunksRef = useRef<{ seq: number; chunk: ArrayBuffer }[]>([]);
  const nextSeqRef = useRef(0);
  // Older live servers never ack, so an unacked chunk only proves a loss once this stream has acked something.
  const acksSeenRef = useRef(false);
  // Audio went out on the current server session, so losing that session loses part of the take.
  const streamHasAudioRef = useRef(false);
  const takeIncompleteRef = useRef(false);
  // Bumped by clearWords() to force effect re-run and establish a fresh socket.
  const [sessionId, setSessionId] = useState(0);

  // handleWord is stable ([] deps) — no state/prop deps so the socket effect doesn't rebuild it.
  const handleWord = useCallback((word: LiveWordEntity) => {
    if (!word.text?.trim()) return;

    const tupleKey = `${word.speakerId ?? "unknown"}:${
      word.instances?.startInSec ?? "no-start"
    }`;
    if (seenTuplesRef.current.has(tupleKey)) return;
    seenTuplesRef.current.add(tupleKey);

    const result = processWord(
      word,
      paragraphsRef.current,
      currentParagraphRef.current,
    );

    paragraphsRef.current = result.paragraphs;
    currentParagraphRef.current = result.currentParagraph;
    setParagraphs([...result.paragraphs]);
  }, []);

  const updateReconnectStatus = useCallback((status: "idle" | "reconnecting" | "exhausted") => {
    reconnectStatusRef.current = status;
    setReconnectStatus(status);
  }, []);

  const isStopUnconfirmed = useCallback(() => {
    const stopSeq = stopSeqRef.current;
    const oldestUnacked = unackedChunksRef.current[0];
    return stopSeq !== null && acksSeenRef.current && oldestUnacked !== undefined && oldestUnacked.seq < stopSeq;
  }, []);

  const syncUndeliveredAudio = useCallback(() => {
    setHasUndeliveredAudio(takeIncompleteRef.current || pendingStopRef.current || isStopUnconfirmed());
  }, [isStopUnconfirmed]);

  const markTakeIncomplete = useCallback(() => {
    takeIncompleteRef.current = true;
    syncUndeliveredAudio();
  }, [syncUndeliveredAudio]);

  // The server acks the highest seq it holds, so everything up to it is safe to drop.
  const sendChunk = useCallback((seq: number, chunk: ArrayBuffer) => {
    socketRef.current?.emit(EVENTS.AUDIO_DATA, chunk, seq, (ackedSeq: unknown) => {
      if (typeof ackedSeq !== "number") return;
      acksSeenRef.current = true;
      unackedChunksRef.current = unackedChunksRef.current.filter((item) => item.seq > ackedSeq);
      if (stopSeqRef.current !== null) syncUndeliveredAudio();
    });
  }, [syncUndeliveredAudio]);

  const sendAudioData = useCallback(
    (audioBuffer: ArrayBuffer) => {
      const socket = socketRef.current;
      // No live socket (live transcription off), so nothing would ever ack this chunk
      if (!socket) return;
      const seq = nextSeqRef.current++;
      unackedChunksRef.current.push({ seq, chunk: audioBuffer });
      streamHasAudioRef.current = true;
      if (unackedChunksRef.current.length > MAX_UNACKED_CHUNKS) {
        unackedChunksRef.current.shift();
        if (acksSeenRef.current) markTakeIncomplete();
      }
      if (socket.connected) sendChunk(seq, audioBuffer);
    },
    [sendChunk, markTakeIncomplete],
  );

  const cancelPendingReconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const cancelStopGrace = useCallback(() => {
    if (stopGraceTimeoutRef.current) {
      clearTimeout(stopGraceTimeoutRef.current);
      stopGraceTimeoutRef.current = null;
    }
  }, []);

  // Emits STOP now so trailing words start flushing, and closes this exact socket after the grace period.
  const sendStop = useCallback((sock: Socket) => {
    stopSeqRef.current = nextSeqRef.current;
    pendingStopRef.current = false;
    deliberateStopRef.current = true;
    sock.emit(EVENTS.STOP_TRANSCRIPTION);
    syncUndeliveredAudio();
    stopGraceTimeoutRef.current = setTimeout(() => {
      stopGraceTimeoutRef.current = null;
      if (isStopUnconfirmed()) takeIncompleteRef.current = true;
      stopSeqRef.current = null;
      syncUndeliveredAudio();
      sock.disconnect();
    }, STOP_GRACE_MS);
  }, [isStopUnconfirmed, syncUndeliveredAudio]);

  const stopTranscription = useCallback(() => {
    const sock = socketRef.current;
    if (!sock || deliberateStopRef.current || pendingStopRef.current) return;
    if (sock.connected) {
      sendStop(sock);
      return;
    }
    if (reconnectStatusRef.current === "reconnecting") {
      pendingStopRef.current = true;
      syncUndeliveredAudio();
      return;
    }
    // Nothing will reconnect (exhausted or never connected), so audio since the drop cannot be delivered
    deliberateStopRef.current = true;
    cancelPendingReconnect();
    sock.disconnect();
    if (streamHasAudioRef.current) markTakeIncomplete();
  }, [sendStop, cancelPendingReconnect, syncUndeliveredAudio, markTakeIncomplete]);

  // Resets all session state so the next recording starts clean.
  const clearWords = useCallback(() => {
    paragraphsRef.current = [];
    currentParagraphRef.current = undefined;
    seenTuplesRef.current.clear();
    // nextSeq is not reset: if this socket's server session continues, a lower seq would be dropped as a repeat
    unackedChunksRef.current = [];
    streamHasAudioRef.current = false;
    takeIncompleteRef.current = false;
    stopSeqRef.current = null;
    setHasUndeliveredAudio(false);
    setTakeMediaIds([]);
    setMediaData(null);
    setParagraphs([]);
    setIsComplete(false);
    // A reconnect still in progress is left running; a socket that will not reconnect on its own is replaced
    if (deliberateStopRef.current || pendingStopRef.current || reconnectStatusRef.current === "exhausted") {
      deliberateStopRef.current = false;
      pendingStopRef.current = false;
      cancelPendingReconnect();
      cancelStopGrace();
      reconnectAttemptsRef.current = 0;
      updateReconnectStatus("idle");
      setSessionId((n) => n + 1);
    }
  }, [cancelPendingReconnect, cancelStopGrace, updateReconnectStatus]);

  useEffect(() => {
    if (!enabled) return;
    // apiKey is required for live streams. Surface the real cause immediately rather than
    // letting awaitSocketReady time out 5s later with a generic "failed to initialize" message.
    const isLiveStream = !mediaId;
    if (isLiveStream && !apiKey) {
      setError(
        "Live transcription is unavailable: no API key is associated with your account.",
      );
      return;
    }
    setError(null);

    const queryParams: Record<string, string> = {};
    if (isLiveStream) {
      queryParams["speak-api-key"] = apiKey;
      // Use locked language from first connect so reconnects send the same query string.
      queryParams.sourceLanguage = lockedLanguageRef.current ?? sourceLanguage;
      queryParams.mediaType = mediaType;
      if (folderId) queryParams.folderId = folderId;
    }

    const socket = io(`${LIVE_URL}/v1/live`, {
      query: queryParams,
      transports: ["websocket"],
      reconnection: false,
    });

    socketRef.current = socket;

    // Ends a stop that was waiting for a reconnect that can no longer reach the held session
    const abandonPendingStop = () => {
      pendingStopRef.current = false;
      deliberateStopRef.current = true;
      syncUndeliveredAudio();
      socket.disconnect();
    };

    const scheduleReconnect = () => {
      if (!enabled) return;
      // connect_error and disconnect can both report one failed attempt
      if (reconnectTimeoutRef.current) return;
      const attempt = reconnectAttemptsRef.current;
      if (attempt >= RECONNECT_BACKOFF_MS.length) {
        updateReconnectStatus("exhausted");
        if (streamHasAudioRef.current) markTakeIncomplete();
        if (pendingStopRef.current) abandonPendingStop();
        return;
      }
      const delay = RECONNECT_BACKOFF_MS[attempt];
      reconnectAttemptsRef.current = attempt + 1;
      updateReconnectStatus("reconnecting");
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        if (socketRef.current && !socketRef.current.connected) {
          socketRef.current.connect();
        }
      }, delay);
    };

    socket.on(RESPONSE_TYPES.CONNECT, () => {
      // Lock language on first connect; subsequent reconnects ignore this branch.
      if (isLiveStream && lockedLanguageRef.current === null) {
        lockedLanguageRef.current = sourceLanguage;
      }
      reconnectAttemptsRef.current = 0;
      // A recovered socket keeps its server session; the words it missed were replayed before this event
      if (!socket.recovered) seenTuplesRef.current.clear();
      updateReconnectStatus("idle");
      setIsConnected(true);
      setError(null);

      if (isLiveStream) {
        if (socket.recovered) {
          for (const { seq, chunk } of unackedChunksRef.current) sendChunk(seq, chunk);
          if (pendingStopRef.current) sendStop(socket);
        } else {
          // A new server session cannot continue the take that was streaming on the lost one
          if (streamHasAudioRef.current) markTakeIncomplete();
          if (pendingStopRef.current) {
            abandonPendingStop();
            return;
          }
          unackedChunksRef.current = [];
          nextSeqRef.current = 0;
          acksSeenRef.current = false;
          streamHasAudioRef.current = false;
          socket.emit(EVENTS.START_LIVE_STREAM, { apiKey });
        }
      } else if (mediaId) {
        socket.emit(EVENTS.SUBSCRIBE_TO_MEDIA, { mediaId });
      }
    });

    // Skip reconnect when disconnect was triggered by stopTranscription() — session is intentionally over.
    // clearWords() will bump sessionId to establish a fresh socket for the next recording.
    socket.on(RESPONSE_TYPES.DISCONNECT, (reason: Socket.DisconnectReason) => {
      setIsConnected(false);
      socket.sendBuffer = socket.sendBuffer.filter(
        (packet) => !EVENTS_RESENT_BY_HOOK.has((packet.data as unknown[] | undefined)?.[0] as string),
      );
      if (deliberateStopRef.current) {
        // A network drop inside the stop grace may have lost the tail and the stop itself
        const isTailAtRisk =
          stopGraceTimeoutRef.current !== null && NETWORK_DISCONNECT_REASONS.has(reason) && isStopUnconfirmed();
        if (!isTailAtRisk) return;
        cancelStopGrace();
        stopSeqRef.current = null;
        deliberateStopRef.current = false;
        pendingStopRef.current = true;
        syncUndeliveredAudio();
      }
      scheduleReconnect();
    });

    socket.on(RESPONSE_TYPES.ERROR, (err: { message?: string } | string) => {
      const msg =
        typeof err === "string" ? err : (err?.message ?? "Connection error");
      setError(msg);
      setIsConnected(false);
    });

    // Surface handshake failures immediately — otherwise the user sees a generic
    // "failed to initialize" from the 5s awaitSocketReady timeout instead of the real WS error.
    socket.on(
      RESPONSE_TYPES.CONNECT_ERROR,
      (err: Error | { message?: string } | string) => {
        const msg =
          typeof err === "string"
            ? err
            : err instanceof Error
              ? err.message
              : (err?.message ?? "Could not connect to live transcription server");
        setError(msg);
        setIsConnected(false);
        // A failed reconnect attempt fires connect_error, not disconnect, so the retry chain continues here
        if (reconnectAttemptsRef.current > 0 && !deliberateStopRef.current) scheduleReconnect();
      },
    );

    socket.on(
      RESPONSE_TYPES.METADATA,
      (data: { folderId: string; mediaId: string; name: string }) => {
        if (data.folderId && data.mediaId) {
          setMediaData(data);
          if (isLiveStream) {
            setTakeMediaIds((ids) => (ids.includes(data.mediaId) ? ids : [...ids, data.mediaId]));
          }
        }
      },
    );

    socket.on(
      RESPONSE_TYPES.TRANSCRIPT,
      (response: {
        word?: LiveWordEntity;
        data?: { word: LiveWordEntity };
      }) => {
        const word = response?.word ?? response?.data?.word;
        if (word) handleWord(word);
      },
    );

    socket.on(RESPONSE_TYPES.CLOSE, () => {
      setIsComplete(true);
      setIsConnected(false);
    });

    return () => {
      cancelPendingReconnect();
      cancelStopGrace();
      reconnectAttemptsRef.current = 0;
      // Reset language lock so next session picks up fresh prop value.
      lockedLanguageRef.current = null;
      seenTuplesRef.current.clear();
      updateReconnectStatus("idle");
      if (mediaId) {
        socket.emit(EVENTS.UNSUBSCRIBE_FROM_MEDIA, { mediaId });
      }
      // Cleanup emits stop-transcription synchronously (no 5s delay — must be synchronous).
      socket.emit(EVENTS.STOP_TRANSCRIPTION);
      socket.disconnect();
      socketRef.current = null;
    };
    // handleWord is stable ([] deps) — excluding it prevents socket rebuilds.
    // sessionId included so clearWords() forces a fresh socket after deliberate stop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, apiKey, sourceLanguage, mediaType, mediaId, folderId, sessionId]);

  return {
    paragraphs,
    isConnected,
    isComplete,
    error,
    mediaData,
    reconnectStatus,
    hasUndeliveredAudio,
    takeMediaIds,
    sendAudioData,
    stopTranscription,
    clearWords,
  };
}
